import React from "react";
import { Loader2, TrendingDown, TrendingUp } from "lucide-react";
import type {
  PriceAlertHistoryListVariant,
  SharedPriceAlertHistoryItem,
} from "./price-alert-history.types";

function formatPrice(price: number | null | undefined): string {
  if (price === null || price === undefined || Number.isNaN(price)) {
    return "—";
  }
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })}`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h24",
  });
}

function venueLabel(exchange: string): string {
  if (!exchange) return "";
  return exchange
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(
      (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    )
    .join(" ");
}

export interface PriceAlertHistoryListProps {
  readonly events: SharedPriceAlertHistoryItem[];
  readonly title: string;
  readonly variant: PriceAlertHistoryListVariant;
  readonly isLoading?: boolean;
  /** Overrides default scroll area for `analyze` (max-h + overflow). */
  readonly listClassName?: string;
}

function HistoryRows({
  events,
}: Readonly<{ events: SharedPriceAlertHistoryItem[] }>) {
  return (
    <div className="space-y-2">
      {events.map((h) => {
        const isSpike = h.alertType.toLowerCase() === "spike";
        const signedPct = `${h.changePercent >= 0 ? "+" : ""}${h.changePercent.toFixed(2)}%`;
        return (
          <div
            key={h.id}
            className="flex items-center justify-between gap-3 rounded-md border border-border/50 bg-primary/60 p-2 sm:p-2.5"
          >
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
              {isSpike ? (
                <TrendingUp className="h-4 w-4 shrink-0 text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 shrink-0 text-red-400" />
              )}
              <span className="shrink-0 text-xs font-medium capitalize text-text-primary">
                {h.alertType}
              </span>
              <span className="text-xs text-text-muted">·</span>
              <span className="truncate text-xs text-text-secondary">
                {venueLabel(h.exchange)}
              </span>
              <span className="text-xs text-text-muted">·</span>
              <span
                className={`shrink-0 text-xs font-medium ${
                  isSpike ? "text-green-400" : "text-red-400"
                }`}
              >
                {signedPct}
              </span>
              <span className="text-xs text-text-muted">·</span>
              <span className="shrink-0 text-xs text-text-secondary">
                {h.interval}
              </span>
            </div>
            <div className="shrink-0 text-right">
              <p className="text-sm font-medium text-text-primary">
                {formatPrice(h.currentPrice)}
              </p>
              <p className="text-xs text-text-secondary">
                {formatDateTime(h.timestamp)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export const PriceAlertHistoryList: React.FC<PriceAlertHistoryListProps> = ({
  events,
  title,
  variant,
  isLoading,
  listClassName,
}) => {
  if (!isLoading && events.length === 0) {
    return null;
  }

  const scrollListClass =
    listClassName ??
    (variant === "analyze" ? "max-h-[200px] overflow-y-auto" : undefined);

  let body: React.ReactNode;
  if (isLoading === true) {
    body = (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-accent" />
      </div>
    );
  } else if (scrollListClass) {
    body = (
      <div className={scrollListClass}>
        <HistoryRows events={events} />
      </div>
    );
  } else {
    body = <HistoryRows events={events} />;
  }

  if (variant === "blog") {
    return (
      <section className="rounded-xl border border-border bg-secondary/90 p-6">
        <h2 className="mb-3 text-xl font-semibold text-text-primary">{title}</h2>
        {body}
      </section>
    );
  }

  if (variant === "drawer") {
    return (
      <div className="space-y-3 rounded-lg border border-gray-500/30 bg-primary/40 p-3">
        <h3 className="text-sm font-bold text-text-primary">{title}</h3>
        {body}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-gray-500/20 bg-black/30 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-text-secondary">
        {title}
      </p>
      {body}
    </div>
  );
};
